import { useState, useEffect, useMemo } from 'react';
import { Modal } from 'antd';
import { useAppNavigate } from '@/hooks';
import SystemHeader from '@/widgets/layout/SystemHeader';
import { BackButton } from '@/widgets/demo/ListPrimitives';
import { demoButtonClass } from '@/widgets/demo/listHelpers';
import { cn } from '@/utils/cn';
import { ocrCaseFieldValuesApi } from '@/services/municipalities/api';
import { importBatchesApi } from '@/services/importBatches/api';
import { isHandledAuthError } from '@/services/client';
import { useToast } from '@/widgets/common';
import type {
  OcrCaseApprovalRequest,
  OcrCaseApprovalResponse,
  OcrCaseFieldValueResponse,
  OcrMatchCandidate,
  OcrMatchCandidatesResponse,
} from '@/services/municipalities/types';
import type { OcrCaseSummaryResponse } from '@/services/importBatches/types';

interface ApplicationReviewProps {
  initialCaseIndex?: number;
  /** データ取込履歴から遷移したときの取込バッチ ID（確認 UI は未接続の場合あり） */
  importBatchId?: number | null;
  /** 実 DB の OCR 案件 ID。指定すると compareFields を API から動的に取得・保存する。 */
  ocrCaseId?: number | null;
  parentCrumb?: { label: string; page?: string };
  pageTitle?: string;
  subjectIdentId?: string;
}

/* ========================================================
   申請審査画面（紙・電子対比レビュー対応）
======================================================== */
const OCR_PREVIEW_BODY_PX = 600;
const DISPLAY_ONLY_SYSTEM_KEY_PREFIX = 'ocr_display.';
const INTERNAL_SYSTEM_KEY_PREFIX = 'internal.';
// 旧「保護台帳」プレビュー雛形は実 PDF 表示に置き換え済みで未使用。再有効化用に残置する。
const SHOW_LEGACY_FORM_PREVIEW = false;

const isDisplayOnlySystemKey = (systemKey?: string | null) =>
  !!systemKey && systemKey.startsWith(DISPLAY_ONLY_SYSTEM_KEY_PREFIX);

const isHiddenSystemKey = (systemKey?: string | null) =>
  !!systemKey && systemKey.startsWith(INTERNAL_SYSTEM_KEY_PREFIX);

const IconDocBlue = () => (
  <svg className="h-4 w-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
  </svg>
);
const IconPencilIndigo = () => (
  <svg className="h-[18px] w-[18px] text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);
const IconCheckPurple = () => (
  <svg className="h-[18px] w-[18px] text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);
const IconFileOrange = () => (
  <svg className="h-4 w-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
  </svg>
);
const IconOcrHeader = () => (
  <svg className="mr-1.5 h-4 w-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
  </svg>
);
const ChevronLeft = () => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
);
const ChevronRight = () => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
);
const ArrowRightBtn = () => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
);

const ApplicationReview = ({ initialCaseIndex = 0, importBatchId, ocrCaseId, parentCrumb, pageTitle, subjectIdentId }: ApplicationReviewProps) => {
  const navigate = useAppNavigate();
  const toast = useToast();
  const [recipientComment, setRecipientComment] = useState('');
  const [compareZoom, setCompareZoom] = useState(100);
  const [fieldChecks, setFieldChecks] = useState<Record<string, boolean>>({});
  const [fieldEdits, setFieldEdits] = useState<Record<string, string>>({});
  const [currentCase, setCurrentCase] = useState(initialCaseIndex);
  const [remoteFields, setRemoteFields] = useState<OcrCaseFieldValueResponse[] | null>(null);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const [remoteSaving, setRemoteSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [caseErrorMessage, setCaseErrorMessage] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [cases, setCases] = useState<OcrCaseSummaryResponse[] | null>(null);
  // 申出書 OCR 照合（③）: 承認時に取得した既存受給者候補と、職員の決定モーダル。
  const [matchCandidates, setMatchCandidates] = useState<OcrMatchCandidatesResponse | null>(null);
  const [showMatchModal, setShowMatchModal] = useState(false);
  // 一次選択（新規 / 既存に紐付け）＋ 既存選択時のターゲット受給者・上書き更新フラグ・不可候補の折り畳み。
  const [primaryChoice, setPrimaryChoice] = useState<'NEW' | 'EXISTING'>('NEW');
  const [selectedRecipientId, setSelectedRecipientId] = useState<number | null>(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [showBlocked, setShowBlocked] = useState(false);
  // 既存受給者の手動検索（③補助）
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<OcrMatchCandidate[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchPage, setSearchPage] = useState(0);
  const [searchHasMore, setSearchHasMore] = useState(false);
  const [searchDone, setSearchDone] = useState(false);

  const effectiveCaseId: number | null = useMemo(() => {
    if (cases && cases.length > 0) {
      const clamped = Math.min(Math.max(currentCase, 0), cases.length - 1);
      return cases[clamped]?.id ?? null;
    }
    return ocrCaseId ?? null;
  }, [cases, currentCase, ocrCaseId]);

  useEffect(() => {
    // 取込バッチIDの有無で OCR 案件一覧を取得/クリアする正当なデータ取得 effect。
    /* eslint-disable react-hooks/set-state-in-effect */
    if (importBatchId == null) {
      setCases(null);
      setCaseErrorMessage(null);
      return;
    }
    /* eslint-enable react-hooks/set-state-in-effect */
    let cancelled = false;
    importBatchesApi
      .listOcrCases(importBatchId)
      .then(({ data }) => {
        if (cancelled) return;
        setCases(data);
        if (ocrCaseId != null) {
          const idx = data.findIndex((c) => c.id === ocrCaseId);
          if (idx >= 0) setCurrentCase(idx);
        }
      })
      .catch((err) => {
        if (isHandledAuthError(err)) return;
        if (!cancelled) {
          setCases([]);
          setCaseErrorMessage(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [importBatchId, ocrCaseId]);

  useEffect(() => {
    // 親から渡る初期ケース番号をローカル state に同期する。
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCurrentCase(initialCaseIndex);
  }, [initialCaseIndex]);

  useEffect(() => {
    // PDF はケース選択に応じて取得/破棄する正当なデータ取得 effect。
    /* eslint-disable react-hooks/set-state-in-effect */
    if (effectiveCaseId == null) {
      setPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setPdfError(null);
      return;
    }
    let cancelled = false;
    let createdUrl: string | null = null;
    setPdfLoading(true);
    setPdfError(null);
    /* eslint-enable react-hooks/set-state-in-effect */
    ocrCaseFieldValuesApi
      .downloadPdf(effectiveCaseId)
      .then(({ data }) => {
        if (cancelled) return;
        const blob = data instanceof Blob ? data : new Blob([data as ArrayBuffer], { type: 'application/pdf' });
        createdUrl = URL.createObjectURL(blob);
        setPdfUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return createdUrl;
        });
      })
      .catch((err) => {
        if (isHandledAuthError(err)) return;
        if (!cancelled) {
          setPdfUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return null;
          });
          setPdfError('PDF の取得に失敗しました。');
        }
      })
      .finally(() => {
        if (!cancelled) setPdfLoading(false);
      });
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [effectiveCaseId]);

  useEffect(() => {
    return () => {
      setPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, []);

  useEffect(() => {
    // フィールド値はケース選択に応じて取得/破棄する正当なデータ取得 effect。
    /* eslint-disable react-hooks/set-state-in-effect */
    if (effectiveCaseId == null) {
      setRemoteFields(null);
      return;
    }
    let cancelled = false;
    setRemoteLoading(true);
    /* eslint-enable react-hooks/set-state-in-effect */
    ocrCaseFieldValuesApi
      .list(effectiveCaseId)
      .then(({ data }) => {
        if (cancelled) return;
        const visibleFields = data.filter((field) => !isHiddenSystemKey(field.systemKey));
        setRemoteFields(visibleFields);
        const seededChecks: Record<string, boolean> = {};
        const seededEdits: Record<string, string> = {};
        visibleFields.forEach((field, index) => {
          const key = `0-${index}`;
          if (field.confirmed) seededChecks[key] = true;
          if (field.correctedValue != null && field.correctedValue !== (field.ocrValue ?? '')) {
            seededEdits[key] = field.correctedValue;
          }
        });
        setFieldChecks(seededChecks);
        setFieldEdits(seededEdits);
      })
      .catch((err) => {
        if (isHandledAuthError(err)) return;
        if (!cancelled) setRemoteFields([]);
      })
      .finally(() => {
        if (!cancelled) setRemoteLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveCaseId]);

  const demoReviewQueue = useMemo(
    () => [
      { identId: 'SID-U-123456', id: 'AP-2024-0031', name: '山田 太郎', household: 'W-123456', date: '2024/02/10', type: '保護台帳確認', done: false, reviewerName: null },
      { identId: 'SID-U-123890', id: 'AP-2024-0032', name: '鈴木 花子', household: 'W-123890', date: '2024/02/10', type: '保護台帳確認', done: true, reviewerName: '佐藤 花子' },
    ],
    [],
  );

  type QueueItem = { identId: string; id: string; name: string; household: string; date: string; type: string; done: boolean; reviewerName: string | null };
  type CompareField = { label: string; value: string; systemKey: string | null; displayOnly: boolean };

  const reviewQueue: QueueItem[] = useMemo(() => {
    if (cases && cases.length > 0) {
      return cases.map((c) => ({
        identId: c.identId ?? '—',
        id: c.applicationNo ?? `#${c.id}`,
        name: c.applicantName ?? '—',
        household: c.householdNo ?? '—',
        date: c.importDate ?? '—',
        type: c.caseType ?? '保護台帳確認',
        done: c.reviewed,
        reviewerName: c.reviewerName,
      }));
    }
    if (importBatchId == null) {
      return demoReviewQueue;
    }
    return [];
  }, [cases, importBatchId, demoReviewQueue]);

  const totalCases = reviewQueue.length;
  const doneCases = reviewQueue.filter((c) => c.done).length;
  const safeCurrentCase = Math.min(Math.max(currentCase, 0), Math.max(totalCases - 1, 0));
  const caseData: QueueItem = reviewQueue[safeCurrentCase] ?? {
    identId: '—', id: '—', name: '—', household: '—', date: '—', type: '保護台帳確認', done: false, reviewerName: null,
  };
  const isReadOnly = caseData.done;
  // 照合（③④⑤）は申出書 OCR ケースのみ。保護台帳（台帳確認画面）は照合せず従来どおり直接承認（＝挙動不変）。
  const isMoushideshoCase = (caseData.type ?? '').includes('申出書');

  useEffect(() => {
    // 選択中ケースのエラーメッセージを表示用 state に同期する。
    /* eslint-disable react-hooks/set-state-in-effect */
    if (effectiveCaseId == null || !cases) {
      setCaseErrorMessage(null);
      return;
    }
    const current = cases.find((c) => c.id === effectiveCaseId);
    setCaseErrorMessage(current?.errorMessage ?? null);
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [cases, effectiveCaseId]);
  // 画面上の修正値・確認チェックが保存済み状態（remoteFields）と異なるか。
  // 翻頁で編集 state を破棄する前の確認ダイアログ用（デモ表示・確認済ケースは常に false）。
  const hasUnsavedChanges = useMemo(() => {
    if (!remoteFields || isReadOnly) return false;
    return remoteFields.some((field, index) => {
      if (!field.systemKey || isDisplayOnlySystemKey(field.systemKey)) return false;
      const fKey = `0-${index}`;
      const savedValue = field.correctedValue ?? field.ocrValue ?? '';
      const currentValue = fieldEdits[fKey] !== undefined ? fieldEdits[fKey] : (field.ocrValue ?? '');
      if (currentValue !== savedValue) return true;
      return !!fieldChecks[fKey] !== !!field.confirmed;
    });
  }, [remoteFields, isReadOnly, fieldEdits, fieldChecks]);

  const resetMatchSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setSearchLoading(false);
    setSearchPage(0);
    setSearchHasMore(false);
    setSearchDone(false);
  };
  const moveToCase = (index: number) => {
    setCurrentCase(index);
    setFieldChecks({});
    setFieldEdits({});
    setShowMatchModal(false);
    setMatchCandidates(null);
    setPrimaryChoice('NEW');
    setSelectedRecipientId(null);
    setUpdateExisting(false);
    setShowBlocked(false);
    resetMatchSearch();
  };
  const confirmDiscardIfDirty = (proceed: () => void) => {
    if (!hasUnsavedChanges) {
      proceed();
      return;
    }
    Modal.confirm({
      title: '未保存の修正があります',
      content: '修正値・確認チェックが保存されていません。保存せずに移動すると破棄されます。',
      okText: '保存せずに移動',
      okButtonProps: { danger: true },
      cancelText: 'キャンセル',
      onOk: proceed,
    });
  };
  const goNext = () => { if (currentCase < totalCases - 1) confirmDiscardIfDirty(() => moveToCase(currentCase + 1)); };
  const goPrev = () => { if (currentCase > 0) confirmDiscardIfDirty(() => moveToCase(currentCase - 1)); };

  const toggleFieldCheck = (key: string) => setFieldChecks(prev => ({ ...prev, [key]: !prev[key] }));
  const updateFieldValue = (key: string, val: string) => setFieldEdits(prev => ({ ...prev, [key]: val }));

  const demoCompareFields = useMemo(
    () => [
      { label: 'ケース番号', value: '12345', systemKey: null as string | null },
      { label: '世帯主氏名', value: '山田 太郎', systemKey: null },
      { label: '世帯主居住地現在地', value: '東京都新宿区西新宿2-8-1', systemKey: null },
      { label: '備考', value: '面談実施', systemKey: null },
      { label: '世帯員1 世帯員識別符号', value: '山田 太郎', systemKey: null },
      { label: '世帯員1 年齢', value: '54', systemKey: null },
      { label: '世帯員1 生年月日', value: '1970/03/15', systemKey: null },
      { label: '世帯員2 世帯員識別符号', value: '山田 花子', systemKey: null },
      { label: '世帯員2 年齢', value: '51', systemKey: null },
      { label: '世帯員2 生年月日', value: '1973/08/22', systemKey: null },
    ],
    [],
  );

  // 実案件（取込バッチ/OCR 案件 ID 付きで遷移）ではデモ値を出さない。デモ値は純粋なデモ表示専用。
  const isDemoMode = importBatchId == null && ocrCaseId == null;

  const compareFields = useMemo<CompareField[]>(() => {
    if (remoteFields !== null) {
      return remoteFields.map((field) => ({
        label: field.label,
        value: field.ocrValue ?? '',
        systemKey: field.systemKey as string | null,
        displayOnly: isDisplayOnlySystemKey(field.systemKey),
      }));
    }
    if (!isDemoMode) {
      return [];
    }
    return demoCompareFields.map((field) => ({ ...field, displayOnly: false }));
  }, [remoteFields, demoCompareFields, isDemoMode]);

  const currentFields = compareFields;
  const checkableFieldCount = currentFields.filter((field) => !field.displayOnly).length;
  const checkedCount = currentFields.filter((field, i) => !field.displayOnly && fieldChecks[`0-${i}`]).length;
  const allCheckableFieldsChecked = checkableFieldCount > 0 && checkedCount === checkableFieldCount;
  const editedCount = currentFields.filter((field, i) => !field.displayOnly && fieldEdits[`0-${i}`] !== undefined && fieldEdits[`0-${i}`] !== currentFields[i]?.value).length;

  const handleBulkCheck = () => {
    const allChecked = allCheckableFieldsChecked;
    currentFields.forEach((field, i) => {
      if (field.displayOnly) return;
      const fKey = `0-${i}`;
      if (allChecked) { if (fieldChecks[fKey]) toggleFieldCheck(fKey); }
      else { if (!fieldChecks[fKey]) toggleFieldCheck(fKey); }
    });
  };

  // 保存の成否を返す（承認フローは false のとき承認へ進まない）。標準の「保存」ボタンは戻り値を無視してよい。
  const saveRemoteFieldValues = async (): Promise<boolean> => {
    if (effectiveCaseId == null || !remoteFields) return false;
    const updates = remoteFields
      .map((field, index) => {
        if (!field.systemKey || isDisplayOnlySystemKey(field.systemKey)) return null;
        const fKey = `0-${index}`;
        const correctedValue = fieldEdits[fKey];
        const confirmed = !!fieldChecks[fKey];
        return {
          systemKey: field.systemKey,
          correctedValue: correctedValue !== undefined ? correctedValue : (field.correctedValue ?? null),
          confirmed,
        };
      })
      .filter((u): u is NonNullable<typeof u> => u != null);
    setRemoteSaving(true);
    try {
      const { data } = await ocrCaseFieldValuesApi.update(effectiveCaseId, updates);
      setRemoteFields(data.filter((field) => !isHiddenSystemKey(field.systemKey)));
      toast.success('修正値・確認済フラグを保存しました。');
      return true;
    } catch (err) {
      // 認証エラーは共通ハンドラ、それ以外はグローバルのエラートーストが出る。いずれも保存は失敗。
      if (isHandledAuthError(err)) return false;
      return false;
    } finally {
      setRemoteSaving(false);
    }
  };

  // 承認レスポンスを受けて一覧 state 反映・結果別トースト・次ケースへの遷移を行う。
  const finishApprove = (data: OcrCaseApprovalResponse) => {
    // 承認結果を一覧 state に即時反映する。前の台帳で戻ってきた場合も
    // 確認済（読取専用）として表示し、確認完了/残の計数も最新化する。
    // スキップ（登録しない）は reviewed にしない（未確認のまま残す）。
    const skipped = data.matchResultCode === 4;
    if (!skipped) {
      setCases((prev) => (prev ? prev.map((c) => (c.id === effectiveCaseId ? { ...c, reviewed: true } : c)) : prev));
    }

    if (data.alreadyRegistered) {
      toast.info('この案件は既に承認済みです。');
    } else if (skipped) {
      toast.info('この案件はスキップしました（登録していません）。');
    } else if (data.matchResultName) {
      // 照合結果別（新規登録／既存受給者に紐付け／既存申出書を更新）
      toast.success(`${data.matchResultName}しました (受給者ID: ${data.recipientId ?? '-'})`);
    } else {
      toast.success(`受給者として登録しました (ID: ${data.recipientId ?? '-'})`);
    }

    if (data.batchCompleted) {
      toast.success('バッチ内の全案件が承認完了しました。');
      navigate(parentCrumb?.page || 'ocr-read-list');
    } else if (currentCase < totalCases - 1) {
      // 承認直前に保存済みのため、破棄確認（confirmDiscardIfDirty）は経由せず直接移動する
      moveToCase(currentCase + 1);
    }
  };

  // 確定 API 呼び出し（照合の決定 request 付き）。モーダルの「確定」と、候補なし時の直接承認から呼ばれる。
  const doApprove = async (request?: OcrCaseApprovalRequest) => {
    if (effectiveCaseId == null) return;
    setApproving(true);
    try {
      const { data } = await ocrCaseFieldValuesApi.approve(effectiveCaseId, request);
      setShowMatchModal(false);
      setMatchCandidates(null);
      finishApprove(data);
    } catch (err) {
      if (isHandledAuthError(err)) return;
    } finally {
      setApproving(false);
    }
  };

  // 保存 →（申出書のみ）照合候補取得 → 同名候補あり: 登録先確認モーダル / 候補なし・保護台帳: そのまま新規承認＋次へ。
  const handleApproveAndNext = async () => {
    if (!allCheckableFieldsChecked) {
      toast.warning('すべての確認対象項目にチェックを入れてください。');
      return;
    }
    if (effectiveCaseId == null) {
      Modal.error({ content: '案件 ID が不明なため承認できません。' });
      return;
    }
    setApproving(true);
    try {
      const hasPendingEdits =
        Object.keys(fieldEdits).some((k) => fieldEdits[k] !== undefined) ||
        Object.keys(fieldChecks).some((k) => fieldChecks[k]);
      if (hasPendingEdits && remoteFields) {
        // 保存に失敗したら承認・登録へ進まない（職員の訂正が失われたまま確定するのを防ぐ）。
        const saved = await saveRemoteFieldValues();
        if (!saved) {
          setApproving(false);
          return;
        }
      }

      // 照合（③）は申出書 OCR ケースのみ行う。保護台帳 OCR は照合せず（match-candidates も呼ばない）、
      // 下でそのまま直接新規承認する ＝ 台帳確認画面の承認フローは一切変えない。
      if (isMoushideshoCase) {
        let candidates: OcrMatchCandidatesResponse | null = null;
        try {
          candidates = (await ocrCaseFieldValuesApi.matchCandidates(effectiveCaseId)).data;
        } catch (err) {
          // 照合確認の通信/サーバ失敗を「候補ゼロ」と混同して無言で新規作成すると、既存受給者がいる場合に
          // 重複受給者を生成し得る（不可逆）。失敗時は承認を中断し職員に再試行を促す。
          if (isHandledAuthError(err)) {
            setApproving(false);
            return;
          }
          toast.error('照合確認に失敗しました。通信状況を確認して再度お試しください。');
          setApproving(false);
          return;
        }
        // 同名の既存受給者候補がある時だけ「登録先を確認」モーダルを出す（職員が新規/紐付けを確認・選択）。
        // 候補ゼロ（同名の既存受給者なし）はモーダルを出さず、下で直接新規承認する。
        if (candidates && candidates.candidates.length > 0) {
          setMatchCandidates(candidates);
          // 既定: 「更新可・生年月日/住所とも一致」の候補が唯一なら既存紐付けを予選（誤新規＝重複を防ぐ）。
          // それ以外は新規を既定にする。
          const strong = (candidates?.candidates ?? []).filter(
            (c) => c.updatable && c.birthDateMatch && c.addressMatch,
          );
          if (strong.length === 1) {
            setPrimaryChoice('EXISTING');
            setSelectedRecipientId(strong[0].recipientId);
          } else {
            setPrimaryChoice('NEW');
            setSelectedRecipientId(null);
          }
          setUpdateExisting(false);
          setShowBlocked(false);
          resetMatchSearch();
          setShowMatchModal(true);
          setApproving(false);
          return;
        }
      }
    } catch (err) {
      setApproving(false);
      if (isHandledAuthError(err)) return;
      return;
    }

    // 候補なし（同名の既存受給者なし）／保護台帳 OCR → 従来どおり新規作成で承認＋次へ（モーダル非表示）。
    await doApprove({ matchDecision: 'NEW_RECIPIENT' });
  };

  // 既存受給者の手動検索（③補助）。reset=true は新規検索、false は「もっと表示」の次頁追加。
  const runRecipientSearch = async (reset: boolean) => {
    if (effectiveCaseId == null) return;
    const q = searchQuery.trim();
    if (!q) return;
    const nextPage = reset ? 0 : searchPage + 1;
    setSearchLoading(true);
    try {
      const { data } = await ocrCaseFieldValuesApi.searchRecipients(effectiveCaseId, q, nextPage, 20);
      setSearchDone(true);
      setSearchResults((prev) => {
        const base = reset ? [] : prev;
        // 重複除外は「検索結果の中だけ」に限る（＝もっと表示の追加読込用）。
        // 以前は自動候補に出ている recipientId も除外していたが、それだと
        // 「自動候補に既に出ている受給者」を検索したときに結果が空になり、
        // 該当者が居るのに「見つかりませんでした」と表示されてしまう
        // （ケース番号検索で実際に発生。自動候補は同名世帯を列挙するため踏みやすい）。
        // 検索したものは、上の一覧と重複していてもそのまま出す。
        const seen = new Set<number>(base.map((c) => c.recipientId));
        const merged = [...base];
        data.content.forEach((c) => {
          if (!seen.has(c.recipientId)) {
            seen.add(c.recipientId);
            merged.push(c);
          }
        });
        return merged;
      });
      setSearchPage(data.page);
      setSearchHasMore(data.page + 1 < data.totalPages);
    } catch (err) {
      if (isHandledAuthError(err)) return;
      // 通信/サーバ失敗はグローバルのエラートーストが出る。
    } finally {
      setSearchLoading(false);
    }
  };

  // 紐付け先として選択できる候補（自動候補・検索結果で共通）。選ぶと一次選択も「既存に紐付け」へ。
  // 既存の申出書があるときだけ「上書き更新」チェックを出す（＝④更新。既定OFF＝新規追加）。
  const renderSelectableCandidate = (c: OcrMatchCandidate) => {
    const picked = selectedRecipientId === c.recipientId;
    return (
      <div
        key={c.recipientId}
        className={cn('rounded-lg border p-3', picked ? 'border-blue-400 bg-blue-50/40 ring-1 ring-blue-200' : 'border-gray-200')}
      >
        <label className="flex cursor-pointer items-start gap-2">
          <input
            type="radio"
            className="mt-1"
            checked={picked}
            onChange={() => {
              setPrimaryChoice('EXISTING');
              setSelectedRecipientId(c.recipientId);
              setUpdateExisting(false);
            }}
          />
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{c.householdHeadName ?? c.decisionNoticeAddressee ?? '—'}</span>
              {/* 同一ケース番号が複数世帯に割れている場合があるため、候補の特定用にケース番号も表示する。 */}
              {c.registryCaseNumber ? (
                <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-xs text-indigo-700">
                  ケース番号 {c.registryCaseNumber}
                </span>
              ) : null}
              {c.identId ? <span className="text-xs text-gray-500">{c.identId}</span> : null}
              {c.birthDateMatch ? (
                <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-700">生年月日一致</span>
              ) : null}
              {c.addressMatch ? (
                <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-700">住所一致</span>
              ) : null}
              {c.paymentStatusName ? (
                <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">{c.paymentStatusName}</span>
              ) : null}
            </span>
            {c.decisionNoticeAddress ? (
              <span className="mt-0.5 block text-xs text-gray-500">{c.decisionNoticeAddress}</span>
            ) : null}
          </span>
        </label>
        {picked && c.existingApplicationId != null ? (
          <label className="mt-2 flex cursor-pointer items-center gap-2 border-t border-dashed border-gray-200 pl-6 pt-2 text-sm text-gray-700">
            <input type="checkbox" checked={updateExisting} onChange={(e) => setUpdateExisting(e.target.checked)} />
            <span>
              この受給者の既存の申出書を上書き更新する
              <span className="ml-1 text-xs text-gray-400">（既定はOFF＝新しい申出書として追加）</span>
            </span>
          </label>
        ) : null}
      </div>
    );
  };

  // 更新不可（給付額算出以降）の候補。選択不可・理由表示のみ（折り畳みの中に格納）。
  const renderBlockedCandidate = (c: OcrMatchCandidate) => (
    <div key={c.recipientId} className="rounded-lg border border-gray-200 bg-gray-50/60 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-gray-600">{c.householdHeadName ?? c.decisionNoticeAddressee ?? '—'}</span>
        {c.registryCaseNumber ? (
          <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-xs text-indigo-700">
            ケース番号 {c.registryCaseNumber}
          </span>
        ) : null}
        {c.identId ? <span className="text-xs text-gray-400">{c.identId}</span> : null}
        {c.birthDateMatch ? (
          <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-700">生年月日一致</span>
        ) : null}
        {c.addressMatch ? (
          <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-700">住所一致</span>
        ) : null}
        {c.paymentStatusName ? (
          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">{c.paymentStatusName}</span>
        ) : null}
      </div>
      <p className="mt-1 text-xs text-red-600">
        {c.notUpdatableReason ?? 'この受給者は給付額算出以降のため紐付け・更新できません。'}
      </p>
    </div>
  );

  // 確定時の request を一次選択・選択候補・上書き更新フラグから組み立てる。
  const buildMatchRequest = (): OcrCaseApprovalRequest => {
    if (primaryChoice === 'NEW' || selectedRecipientId == null) {
      return { matchDecision: 'NEW_RECIPIENT' };
    }
    const all = [...(matchCandidates?.candidates ?? []), ...searchResults];
    const picked = all.find((c) => c.recipientId === selectedRecipientId);
    if (picked && updateExisting && picked.existingApplicationId != null) {
      return {
        matchDecision: 'UPDATE_EXISTING_APPLICATION',
        targetRecipientId: selectedRecipientId,
        targetApplicationId: picked.existingApplicationId,
      };
    }
    return { matchDecision: 'LINK_EXISTING_RECIPIENT', targetRecipientId: selectedRecipientId };
  };

  const canConfirmMatch = primaryChoice === 'NEW' || selectedRecipientId != null;

  const goRecipientEdit = () =>
    navigate('recipient-edit', {
      detailPageTitle: '受給者詳細情報',
      baseCrumb: parentCrumb?.page
        ? { label: parentCrumb.label, page: parentCrumb.page }
        : { label: '受給者／申出書 検索', page: 'recipient-search' },
    });

  const handleBack = () => {
    navigate(parentCrumb?.page || (pageTitle === '受給者詳細情報' ? 'recipient-search' : 'ocr-read-list'));
  };

  if (pageTitle === '受給者詳細情報') {
    return (
      <div className="min-h-screen bg-gray-100">
        <SystemHeader breadcrumbs={[parentCrumb || { label: '受給者／申出書 検索', page: 'recipient-search' }, { label: '受給者詳細情報' }]} />
        <div className="mx-auto max-w-3xl px-6 py-8">
          <BackButton onClick={handleBack} />
          <div className="mb-6 flex flex-wrap items-center justify-end gap-3">
            <button type="button" onClick={goRecipientEdit} className={`${demoButtonClass.primary} text-sm font-medium`}>
              編集
            </button>
          </div>
          <p className="mb-4 text-sm text-gray-600">識別ID: <span className="font-mono font-medium text-gray-900">{subjectIdentId || '—'}</span></p>

          <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6 shadow-lg">
            <div className="divide-y divide-gray-100">
              {compareFields.map((f, i) => (
                <div key={i} className="flex flex-col gap-2 py-3 first:pt-0 sm:flex-row sm:items-start sm:gap-4">
                  <div className="w-full shrink-0 text-sm font-medium text-gray-600 sm:w-48">{f.label}</div>
                  <div className="flex-1 select-none rounded-lg bg-gray-50 px-3 py-2 font-mono text-sm text-gray-900">{f.value}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6 shadow-lg">
            <label className="mb-2 block text-sm font-medium text-gray-700">コメント</label>
            <textarea
              value={recipientComment}
              onChange={(e) => setRecipientComment(e.target.value)}
              className="h-28 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-blue-500"
              placeholder="コメントを入力してください"
            />
          </div>

          <div className="flex flex-wrap items-center justify-end gap-3">
            <button type="button" onClick={goRecipientEdit} className={`${demoButtonClass.primary} text-sm font-medium`}>
              編集
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <SystemHeader breadcrumbs={[parentCrumb || { label: 'データ取込', page: 'ocr-read-list' }, { label: pageTitle || 'OCR読取結果確認' }]} />
      <div className="mx-auto max-w-screen-2xl px-6 py-6">
        <BackButton onClick={handleBack} />
        {importBatchId != null && (
          <div className="mb-4 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm text-indigo-900">
            取込バッチ ID: <span className="font-mono font-semibold">{importBatchId}</span>
          </div>
        )}
        {caseErrorMessage && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
            <svg className="mt-0.5 h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
            <span className="whitespace-pre-wrap break-all">{caseErrorMessage}</span>
          </div>
        )}

        {/* 審査キューナビ */}
        <div className="mb-5 rounded-xl bg-white p-4 shadow-lg">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={goPrev}
              disabled={safeCurrentCase === 0 || totalCases === 0}
              className={cn(
                'flex items-center space-x-1.5 rounded-lg px-4 py-2 text-sm font-medium transition',
                safeCurrentCase === 0 || totalCases === 0 ? 'cursor-not-allowed text-gray-300' : 'cursor-pointer text-blue-600 hover:bg-blue-50',
              )}
            >
              <ChevronLeft />
              <span>前の台帳</span>
            </button>

            <div className="mx-6 flex-1">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-sm font-bold text-gray-800">{totalCases === 0 ? 0 : safeCurrentCase + 1} / {totalCases} 件目</span>
                  <span className="text-xs text-gray-400">|</span>
                  <span className="text-xs text-gray-500">確認完了: <span className="font-bold text-green-600">{doneCases}</span> / {totalCases} 件</span>
                  <span className="text-xs text-gray-400">|</span>
                  <span className="text-xs text-gray-500">残: <span className="font-bold text-orange-600">{totalCases - doneCases}</span> 件</span>
                </div>
                <span className="font-mono text-xs text-gray-400">{caseData.id}</span>
              </div>
            </div>

            <button
              type="button"
              onClick={goNext}
              disabled={totalCases === 0 || safeCurrentCase >= totalCases - 1}
              className={cn(
                'flex items-center space-x-1.5 rounded-lg px-4 py-2 text-sm font-medium transition',
                totalCases === 0 || safeCurrentCase >= totalCases - 1 ? 'cursor-not-allowed text-gray-300' : 'cursor-pointer text-blue-600 hover:bg-blue-50',
              )}
            >
              <span>次の台帳</span>
              <ChevronRight />
            </button>
          </div>
        </div>

        {/* ヘッダー情報バー */}
        <div className="mb-5 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 p-5 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div className="grid flex-1 grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5 lg:gap-6">
              <div><p className="text-xs text-blue-200">識別ID</p><p className="font-mono text-lg font-bold">{caseData.identId}</p></div>
              <div><p className="text-xs text-blue-200">世帯主</p><p className="text-lg font-bold">{caseData.name}</p></div>
              <div><p className="text-xs text-blue-200">世帯番号</p><p className="font-mono text-lg font-bold">{caseData.household}</p></div>
              <div><p className="text-xs text-blue-200">取込日</p><p className="text-lg font-bold">{caseData.date}</p></div>
              <div><p className="text-xs text-blue-200">確認区分</p><p className="text-lg font-bold">{caseData.type}</p></div>
            </div>
            <div className="ml-4 flex items-center space-x-2">
              <span className={cn(
                'rounded-full border px-3 py-1 text-xs',
                caseData.done ? 'border-green-300/30 bg-green-400/20 text-green-100' : 'border-yellow-300/30 bg-yellow-400/20 text-yellow-100',
              )}>
                {caseData.done ? '確認済' : '未確認'}
              </span>
              {caseData.done && caseData.reviewerName ? (
                <span className="rounded-full bg-white/10 px-3 py-1 text-xs text-blue-100">担当: {caseData.reviewerName}</span>
              ) : null}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-5">
          {/* 左: 紙面スキャン画像 */}
          <div className="col-span-5">
            <div className="overflow-hidden rounded-xl bg-white shadow-lg">
              <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-3">
                <div className="flex items-center space-x-2">
                  <IconFileOrange />
                  <span className="text-sm font-bold text-gray-700">紙面スキャン画像</span>
                </div>
                <div className="flex items-center space-x-1">
                  <button type="button" onClick={() => setCompareZoom(Math.max(50, compareZoom - 10))} className="flex h-7 w-7 cursor-pointer items-center justify-center rounded text-lg text-gray-500 hover:bg-gray-200">−</button>
                  <span className="w-10 text-center text-xs text-gray-500">{compareZoom}%</span>
                  <button type="button" onClick={() => setCompareZoom(Math.min(200, compareZoom + 10))} className="flex h-7 w-7 cursor-pointer items-center justify-center rounded text-lg text-gray-500 hover:bg-gray-200">+</button>
                </div>
              </div>
              <div className="overflow-auto bg-gray-200 p-4" style={{ height: `${OCR_PREVIEW_BODY_PX}px` }}>
                {effectiveCaseId == null ? (
                  <div className="flex h-full items-center justify-center text-sm text-gray-500">
                    PDF を表示するには OCR 案件を選択してください。
                  </div>
                ) : pdfLoading ? (
                  <div className="flex h-full items-center justify-center text-sm text-gray-500">
                    PDF 読込中...
                  </div>
                ) : pdfError ? (
                  <div className="flex h-full items-center justify-center text-sm text-red-600">
                    {pdfError}
                  </div>
                ) : pdfUrl ? (
                  <div
                    style={{
                      width: `${compareZoom}%`,
                      height: `${compareZoom}%`,
                      transform: `scale(${compareZoom / 100})`,
                      transformOrigin: 'top left',
                    }}
                  >
                    <iframe
                      title="OCR 元 PDF"
                      src={pdfUrl}
                      className="block h-full w-full border-0 bg-white"
                      style={{ height: `${OCR_PREVIEW_BODY_PX - 32}px` }}
                    />
                  </div>
                ) : (
                  <div className="flex h-full items-center justify-center text-sm text-gray-500">
                    PDF がありません。
                  </div>
                )}
                {SHOW_LEGACY_FORM_PREVIEW && (
                <div className="mx-auto rounded bg-white p-6 shadow-md" style={{ width: `${Math.round(420 * compareZoom / 100)}px`, minHeight: `${Math.round(580 * compareZoom / 100)}px`, transformOrigin: 'top center' }}>
                  <div style={{ fontSize: `${Math.round(10 * compareZoom / 100)}px` }}>
                    <p className="mb-2" style={{ fontSize: `${Math.round(9 * compareZoom / 100)}px` }}>様式第２号（第２条関係）</p>
                    <div className="mb-3 flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <span className="rounded-full border border-gray-800 px-2 py-0.5" style={{ fontSize: `${Math.round(9 * compareZoom / 100)}px` }}>県費</span>
                        <span className="rounded-full border-2 border-gray-800 px-2 py-0.5 font-bold" style={{ fontSize: `${Math.round(9 * compareZoom / 100)}px` }}>市町村費</span>
                      </div>
                      <p className="font-bold" style={{ fontSize: `${Math.round(16 * compareZoom / 100)}px`, letterSpacing: '0.3em' }}>保護台帳</p>
                      <div className="border border-gray-800 px-2 py-1">
                        <span style={{ fontSize: `${Math.round(9 * compareZoom / 100)}px` }}>ケース番号</span>
                        <span className="ml-1 font-bold">12345</span>
                      </div>
                    </div>
                    <div className="mb-2 border border-gray-800">
                      <div className="grid grid-cols-2 border-b border-gray-800">
                        <div className="border-r border-gray-800">
                          <div className="grid grid-cols-3">
                            <div className="border-r border-gray-800 bg-gray-100 p-1 font-medium">世帯主<br/>{'氏　名'}</div>
                            <div className="col-span-2 p-1 font-bold" style={{ fontSize: `${Math.round(12 * compareZoom / 100)}px` }}>山田 太郎</div>
                          </div>
                        </div>
                        <div>
                          <div className="grid grid-cols-3">
                            <div className="border-r border-gray-800 bg-gray-100 p-1 font-medium">居住地<br/>現在地</div>
                            <div className="col-span-2 p-1">東京都新宿区<br/>西新宿2-8-1</div>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2">
                        <div className="border-r border-gray-800">
                          <div className="grid grid-cols-3">
                            <div className="border-r border-gray-800 bg-gray-100 p-1 font-medium">本籍地</div>
                            <div className="col-span-2 p-1">東京都新宿区<br/>西新宿2-8-1</div>
                          </div>
                        </div>
                        <div>
                          <div className="grid grid-cols-3">
                            <div className="border-r border-gray-800 bg-gray-100 p-1 font-medium">居住の<br/>{'始　期'}</div>
                            <div className="col-span-2 p-1">平成27年 4月 1日</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mb-2 border border-gray-800">
                      <table className="w-full border-collapse" style={{ fontSize: `${Math.round(9 * compareZoom / 100)}px` }}>
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="w-5 border-b border-r border-gray-800 p-0.5 text-center" />
                            <th className="border-b border-r border-gray-800 p-0.5 text-center">{'氏　名'}</th>
                            <th className="border-b border-r border-gray-800 p-0.5 text-center">個人番号</th>
                            <th className="w-6 border-b border-r border-gray-800 p-0.5 text-center">続柄</th>
                            <th className="w-5 border-b border-r border-gray-800 p-0.5 text-center">性別</th>
                            <th className="w-6 border-b border-r border-gray-800 p-0.5 text-center">年齢</th>
                            <th className="border-b border-r border-gray-800 p-0.5 text-center">生年月日</th>
                            <th className="border-b border-r border-gray-800 p-0.5 text-center">学歴</th>
                            <th className="border-b border-gray-800 p-0.5 text-center">心身の状況</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="border-b border-r border-gray-800 p-0.5 text-center">1</td>
                            <td className="border-b border-r border-gray-800 p-0.5 font-bold">山田 太郎</td>
                            <td className="border-b border-r border-gray-800 p-0.5 text-center font-mono">1234-5678-9012</td>
                            <td className="border-b border-r border-gray-800 p-0.5 text-center">世帯主</td>
                            <td className="border-b border-r border-gray-800 p-0.5 text-center">男</td>
                            <td className="border-b border-r border-gray-800 p-0.5 text-center">54</td>
                            <td className="border-b border-r border-gray-800 p-0.5 text-center">S45.3.15</td>
                            <td className="border-b border-r border-gray-800 p-0.5 text-center">高卒</td>
                            <td className="border-b border-gray-800 p-0.5 text-center">良好</td>
                          </tr>
                          <tr>
                            <td className="border-b border-r border-gray-800 p-0.5 text-center">2</td>
                            <td className="border-b border-r border-gray-800 p-0.5 font-bold">山田 花子</td>
                            <td className="border-b border-r border-gray-800 p-0.5 text-center font-mono">9876-5432-1098</td>
                            <td className="border-b border-r border-gray-800 p-0.5 text-center">妻</td>
                            <td className="border-b border-r border-gray-800 p-0.5 text-center">女</td>
                            <td className="border-b border-r border-gray-800 p-0.5 text-center">51</td>
                            <td className="border-b border-r border-gray-800 p-0.5 text-center">S48.8.22</td>
                            <td className="border-b border-r border-gray-800 p-0.5 text-center">高卒</td>
                            <td className="border-b border-gray-800 p-0.5 text-center">良好</td>
                          </tr>
                          {[3, 4, 5].map(n => (
                            <tr key={n}>
                              <td className="border-b border-r border-gray-800 p-0.5 text-center text-gray-300">{n}</td>
                              <td className="border-b border-r border-gray-800 p-0.5" />
                              <td className="border-b border-r border-gray-800 p-0.5" />
                              <td className="border-b border-r border-gray-800 p-0.5" />
                              <td className="border-b border-r border-gray-800 p-0.5" />
                              <td className="border-b border-r border-gray-800 p-0.5" />
                              <td className="border-b border-r border-gray-800 p-0.5" />
                              <td className="border-b border-r border-gray-800 p-0.5" />
                              <td className="border-b border-gray-800 p-0.5" />
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="mb-2 border border-gray-800">
                      <div className="grid grid-cols-2">
                        <div className="border-r border-gray-800">
                          <div className="border-b border-gray-800 bg-gray-100 p-1 text-center font-medium">資産の調</div>
                          <table className="w-full" style={{ fontSize: `${Math.round(8 * compareZoom / 100)}px` }}>
                            <thead>
                              <tr className="bg-gray-50">
                                <th className="border-b border-r border-gray-800 p-0.5">内容</th>
                                <th className="border-b border-r border-gray-800 p-0.5">見積額</th>
                                <th className="border-b border-gray-800 p-0.5">処分の可否</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td className="border-b border-r border-gray-800 bg-gray-50 p-0.5">土地</td>
                                <td className="border-b border-r border-gray-800 p-0.5 text-center">なし</td>
                                <td className="border-b border-gray-800 p-0.5 text-center">—</td>
                              </tr>
                              <tr>
                                <td className="border-b border-r border-gray-800 bg-gray-50 p-0.5">家屋</td>
                                <td className="border-b border-r border-gray-800 p-0.5 text-center">なし</td>
                                <td className="border-b border-gray-800 p-0.5 text-center">—</td>
                              </tr>
                              <tr>
                                <td className="border-r border-gray-800 bg-gray-50 p-0.5">その他</td>
                                <td className="border-r border-gray-800 p-0.5 text-center">なし</td>
                                <td className="p-0.5 text-center">—</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div>
                          <div className="border-b border-gray-800 bg-gray-100 p-1 text-center font-medium">負債の調</div>
                          <table className="w-full" style={{ fontSize: `${Math.round(8 * compareZoom / 100)}px` }}>
                            <thead>
                              <tr className="bg-gray-50">
                                <th className="border-b border-r border-gray-800 p-0.5">種類</th>
                                <th className="border-b border-r border-gray-800 p-0.5">金額</th>
                                <th className="border-b border-gray-800 p-0.5">契約の内容</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td className="border-r border-gray-800 p-0.5 text-center" style={{ height: '30px' }}>—</td>
                                <td className="border-r border-gray-800 p-0.5 text-center">—</td>
                                <td className="p-0.5 text-center">—</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                    <div className="mb-2 border border-gray-800">
                      <div className="border-b border-gray-800 bg-gray-100 p-1 font-medium">住居の状況</div>
                      <table className="w-full" style={{ fontSize: `${Math.round(8 * compareZoom / 100)}px` }}>
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="border-b border-r border-gray-800 p-0.5">自家借家<br/>(間)の別</th>
                            <th className="border-b border-r border-gray-800 p-0.5">規模構造</th>
                            <th className="border-b border-r border-gray-800 p-0.5">建坪</th>
                            <th className="border-b border-r border-gray-800 p-0.5">畳数<br/>室数</th>
                            <th className="border-b border-r border-gray-800 p-0.5">衛生状態</th>
                            <th className="border-b border-r border-gray-800 p-0.5">水道設備</th>
                            <th className="border-b border-gray-800 p-0.5">電灯数</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="border-r border-gray-800 p-1 text-center font-medium">借家</td>
                            <td className="border-r border-gray-800 p-1 text-center">木造2階建</td>
                            <td className="border-r border-gray-800 p-1 text-center">45㎡</td>
                            <td className="border-r border-gray-800 p-1 text-center">6畳<br/>3室</td>
                            <td className="border-r border-gray-800 p-1 text-center">良</td>
                            <td className="border-r border-gray-800 p-1 text-center">有</td>
                            <td className="p-1 text-center">5</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="border border-gray-800">
                      <div className="grid grid-cols-6">
                        <div className="border-r border-gray-800 bg-gray-100 p-1 font-medium">備考</div>
                        <div className="col-span-5 p-1 text-gray-500" style={{ minHeight: '20px' }}>特になし</div>
                      </div>
                    </div>
                  </div>
                </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-span-7 space-y-5">
            <div className="grid grid-cols-3 gap-3">
              <div className="flex items-center space-x-3 rounded-lg bg-white p-3 shadow">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50">
                  <IconDocBlue />
                </div>
                <div><p className="text-xs text-gray-500">全項目</p><p className="text-lg font-bold text-gray-800">{currentFields.length}</p></div>
              </div>
              <div className="flex items-center space-x-3 rounded-lg bg-white p-3 shadow">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50">
                  <IconPencilIndigo />
                </div>
                <div><p className="text-xs text-gray-500">修正済</p><p className="text-lg font-bold text-indigo-700">{editedCount}</p></div>
              </div>
              <div className="flex items-center space-x-3 rounded-lg bg-white p-3 shadow">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-purple-50">
                  <IconCheckPurple />
                </div>
                <div><p className="text-xs text-gray-500">確認済</p><p className="text-lg font-bold text-purple-700">{checkedCount}/{checkableFieldCount}</p></div>
              </div>
            </div>

            <div className="flex flex-col overflow-hidden rounded-xl bg-white shadow-lg">
              <div className="flex shrink-0 items-center justify-between border-b border-gray-200 bg-gray-50 px-5 py-3">
                <h3 className="flex items-center text-sm font-bold text-gray-700">
                  <IconOcrHeader />
                  OCR認識結果チェック
                </h3>
                <span className="text-xs text-gray-500">
                  {remoteLoading ? '読込中...' : '左の紙面画像と照合しながらご確認ください'}
                </span>
              </div>
              <div className="max-h-[600px] divide-y divide-gray-100 overflow-y-auto overscroll-contain" style={{ maxHeight: `${OCR_PREVIEW_BODY_PX}px` }}>
                {currentFields.length === 0 && (remoteLoading || (!isDemoMode && remoteFields === null)) ? (
                  <div className="flex items-center justify-center px-4 py-10 text-sm text-gray-500">読込中...</div>
                ) : null}
                {currentFields.map((f, i) => {
                  const fKey = `0-${i}`;
                  const isDisplayOnly = f.displayOnly;
                  const isEdited = !isDisplayOnly && fieldEdits[fKey] !== undefined && fieldEdits[fKey] !== f.value;
                  const isChecked = !isDisplayOnly && !!fieldChecks[fKey];
                  const currentValue = fieldEdits[fKey] !== undefined ? fieldEdits[fKey] : f.value;
                  return (
                    <div key={i} className={cn('group flex items-center px-4 py-2 transition', isChecked ? 'bg-gray-50/80' : 'hover:bg-blue-50/30')}>
                      <div className="w-8 shrink-0">
                        {!isDisplayOnly ? (
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleFieldCheck(fKey)}
                            disabled={isReadOnly}
                            className="h-4 w-4 cursor-pointer rounded border-gray-300 text-green-600 disabled:cursor-not-allowed disabled:opacity-50"
                          />
                        ) : null}
                      </div>
                      <div className={cn('w-44 shrink-0 text-sm font-medium', isChecked ? 'text-gray-400 line-through' : 'text-gray-700')}>{f.label}</div>
                      <div className="mx-3 flex-1">
                        <input
                          type="text"
                          value={currentValue}
                          readOnly={isReadOnly || isDisplayOnly}
                          onChange={(e) => {
                            if (!isDisplayOnly) updateFieldValue(fKey, e.target.value);
                          }}
                          className={cn(
                            'w-full rounded-lg border px-3 py-1.5 font-mono text-sm transition-all',
                            isDisplayOnly && 'cursor-default border-gray-200 bg-gray-50 text-gray-700',
                            isReadOnly && 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-600',
                            !isDisplayOnly && !isReadOnly && isChecked && 'border-gray-200 bg-gray-50 text-gray-400',
                            !isDisplayOnly && !isReadOnly && !isChecked && isEdited && 'border-indigo-300 bg-indigo-50 text-indigo-800 ring-1 ring-indigo-200',
                            !isDisplayOnly && !isReadOnly && !isChecked && !isEdited && 'border-gray-200 bg-white text-gray-800 focus:border-transparent focus:ring-2 focus:ring-blue-400',
                          )}
                        />
                      </div>
                      <div className="flex w-20 shrink-0 items-center justify-end space-x-1">
                        {isEdited && (
                          <span className="flex items-center whitespace-nowrap rounded bg-indigo-100 px-1.5 py-0.5 text-xs text-indigo-600">
                            <svg className="mr-0.5 h-3 w-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                            修正
                          </span>
                        )}
                        {isChecked && !isEdited && (
                          <span className="flex items-center whitespace-nowrap rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-700">
                            <svg className="mr-0.5 h-3 w-3 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                            OK
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex shrink-0 items-center justify-between border-t border-gray-200 bg-gray-50 px-5 py-3">
                <div className="flex items-center space-x-2 text-xs text-gray-500">
                  <span>確認済: {checkedCount}/{checkableFieldCount}</span>
                  <div className="h-1.5 w-32 overflow-hidden rounded-full bg-gray-200">
                    <div
                      className="h-full rounded-full bg-green-500 transition-all"
                      style={{ width: `${checkableFieldCount > 0 ? (checkedCount / checkableFieldCount) * 100 : 0}%` }}
                    />
                  </div>
                </div>
                {!isReadOnly ? (
                  <button
                    type="button"
                    onClick={handleBulkCheck}
                    disabled={checkableFieldCount === 0}
                    className={cn(
                      'cursor-pointer rounded-lg px-3 py-1.5 text-xs font-medium whitespace-nowrap text-white transition',
                      checkableFieldCount === 0
                        ? 'cursor-not-allowed bg-gray-300 text-gray-500'
                        : checkedCount === checkableFieldCount
                          ? 'bg-gray-500 hover:bg-gray-600'
                          : 'bg-green-600 hover:bg-green-700',
                    )}
                  >
                    {checkableFieldCount > 0 && checkedCount === checkableFieldCount ? '一括チェック解除' : '一括チェック'}
                  </button>
                ) : (
                  <span className="text-xs text-gray-400">確認済みのため変更できません</span>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-white px-5 py-4 shadow-lg">
              <button type="button" onClick={() => navigate('ocr-read-list')} className="cursor-pointer rounded-lg border border-gray-300 px-5 py-2 text-sm hover:bg-gray-50">
                データ取込に戻る
              </button>
              <div className="flex flex-wrap items-center gap-3">
                {remoteFields && effectiveCaseId != null && !isReadOnly ? (
                  <button
                    type="button"
                    onClick={() => void saveRemoteFieldValues()}
                    disabled={remoteSaving}
                    className={cn(
                      'rounded-lg px-5 py-2 text-sm font-medium transition',
                      remoteSaving ? 'cursor-not-allowed bg-gray-300 text-gray-500' : 'cursor-pointer border border-blue-500 text-blue-600 hover:bg-blue-50',
                    )}
                  >
                    {remoteSaving ? '保存中...' : '修正内容を保存'}
                  </button>
                ) : null}
                {isReadOnly ? (
                  <p className="text-sm text-gray-500">このデータは確認完了済みのため、編集・承認操作はできません。</p>
                ) : (
                  <button
                    type="button"
                    disabled={approving || remoteSaving || !allCheckableFieldsChecked}
                    onClick={() => void handleApproveAndNext()}
                    className={cn(
                      'flex items-center space-x-1.5 rounded-lg px-6 py-2 text-sm font-medium shadow-md transition',
                      approving || remoteSaving || !allCheckableFieldsChecked ? 'cursor-not-allowed bg-gray-300 text-gray-500' : 'cursor-pointer bg-blue-600 text-white hover:bg-blue-700',
                    )}
                  >
                    <span>{approving ? '承認中...' : '承認して次の未確認へ'}</span>
                    <ArrowRightBtn />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ③④⑤ この申出書の登録先を確認するモーダル（新規 / 既存に紐付け の2段選択） */}
      <Modal
        open={showMatchModal}
        title="この申出書の登録先を確認"
        width={640}
        confirmLoading={approving}
        okText="この内容で確定"
        cancelText="キャンセル"
        okButtonProps={{ disabled: !canConfirmMatch }}
        onCancel={() => {
          setShowMatchModal(false);
          setMatchCandidates(null);
          setApproving(false);
        }}
        onOk={() => void doApprove(buildMatchRequest())}
      >
        {matchCandidates ? (() => {
          const updatable = matchCandidates.candidates.filter((c) => c.updatable);
          const blocked = matchCandidates.candidates.filter((c) => !c.updatable);
          return (
            <div className="space-y-3 text-sm">
              {/* 申出書の世帯主（＝照合キー）。申出者が世帯主と異なる場合のみ注記。 */}
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-2.5 text-xs text-gray-600">
                申出書の世帯主: <span className="font-medium text-gray-800">{matchCandidates.headName ?? '—'}</span>
                {matchCandidates.headBirthDate ? `（生年月日 ${matchCandidates.headBirthDate}）` : ''}
                {matchCandidates.address ? ` ／ 住所: ${matchCandidates.address}` : ''}
                {matchCandidates.applicantName && matchCandidates.applicantName !== matchCandidates.headName ? (
                  <div className="mt-1">申出者: {matchCandidates.applicantName}（※世帯主と異なる＝代理・後見・家族の可能性）</div>
                ) : null}
              </div>

              <p className="font-semibold text-gray-700">この申出書は、どの受給者のものですか？</p>

              {/* 一次選択①: 新規 */}
              <label
                className={cn(
                  'flex cursor-pointer items-start gap-3 rounded-xl border p-3',
                  primaryChoice === 'NEW' ? 'border-blue-400 bg-blue-50/40' : 'border-gray-200',
                )}
              >
                <input
                  type="radio"
                  className="mt-0.5"
                  checked={primaryChoice === 'NEW'}
                  onChange={() => { setPrimaryChoice('NEW'); setSelectedRecipientId(null); }}
                />
                <span>
                  <span className="font-medium text-gray-800">新しい受給者として登録する</span>
                  <span className="ml-2 text-xs text-gray-500">同じ人がまだ登録されていない場合はこちら（既定）</span>
                </span>
              </label>

              {/* 一次選択②: 既存に紐付け */}
              <label
                className={cn(
                  'flex cursor-pointer items-start gap-3 rounded-xl border p-3',
                  primaryChoice === 'EXISTING' ? 'border-blue-400 bg-blue-50/40' : 'border-gray-200',
                )}
              >
                <input
                  type="radio"
                  className="mt-0.5"
                  checked={primaryChoice === 'EXISTING'}
                  onChange={() => setPrimaryChoice('EXISTING')}
                />
                <span>
                  <span className="font-medium text-gray-800">既存の受給者に紐付ける</span>
                  <span className="ml-2 text-xs text-gray-500">下から対象の受給者を選んでください</span>
                </span>
              </label>

              {/* 「既存に紐付け」選択時のみ展開: 候補（選択可）＋不可候補の折り畳み＋検索 */}
              {primaryChoice === 'EXISTING' ? (
                <div className="ml-4 space-y-2 border-l-2 border-blue-100 pl-3">
                  {updatable.map(renderSelectableCandidate)}
                  {updatable.length === 0 && searchResults.length === 0 ? (
                    <p className="text-xs text-gray-500">紐付け可能な候補がありません。下の検索で受給者を探してください。</p>
                  ) : null}

                  {blocked.length > 0 ? (
                    <div>
                      <button
                        type="button"
                        onClick={() => setShowBlocked((v) => !v)}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        {showBlocked ? '▾' : '▸'} 紐付けできない候補 {blocked.length}件（給付額算出以降のため不可）{showBlocked ? 'を隠す' : 'を表示'}
                      </button>
                      {showBlocked ? <div className="mt-2 space-y-2">{blocked.map(renderBlockedCandidate)}</div> : null}
                    </div>
                  ) : null}

                  {matchCandidates.candidatesTruncated ? (
                    <p className="text-xs text-amber-700">同名の候補が多いため上位のみ表示しています。検索で絞り込めます。</p>
                  ) : null}

                  {/* 手動検索（氏名・住所・識別ID・ケース番号）。
                      自動照合は世帯主氏名の完全一致のため、外字・異体字で字体が異なると候補が出ない。
                      その場合はケース番号で特定できるよう検索対象に含めている。 */}
                  <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-3">
                    <p className="mb-2 text-xs text-gray-500">見つからない場合は検索（氏名・住所・識別ID・ケース番号）</p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') void runRecipientSearch(true); }}
                        placeholder="氏名・住所・識別ID・ケース番号 で検索"
                        className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-400 focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => void runRecipientSearch(true)}
                        disabled={searchLoading || !searchQuery.trim()}
                        className={cn(
                          'shrink-0 rounded-lg px-4 py-1.5 text-sm font-medium',
                          searchLoading || !searchQuery.trim()
                            ? 'cursor-not-allowed bg-gray-200 text-gray-400'
                            : 'cursor-pointer bg-blue-600 text-white hover:bg-blue-700',
                        )}
                      >
                        {searchLoading ? '検索中...' : '検索'}
                      </button>
                    </div>
                    {searchResults.length > 0 ? (
                      <div className="mt-3 space-y-2">
                        {searchResults.map((c) => (c.updatable ? renderSelectableCandidate(c) : renderBlockedCandidate(c)))}
                        {searchHasMore ? (
                          <button
                            type="button"
                            onClick={() => void runRecipientSearch(false)}
                            disabled={searchLoading}
                            className="w-full rounded-lg border border-gray-300 py-1.5 text-xs text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed"
                          >
                            {searchLoading ? '読込中...' : 'もっと表示'}
                          </button>
                        ) : null}
                      </div>
                    ) : searchDone && !searchLoading ? (
                      <p className="mt-2 text-xs text-gray-500">該当する受給者が見つかりませんでした。</p>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          );
        })() : null}
      </Modal>
    </div>
  );
};

export default ApplicationReview;
